import { RefObject, useState } from "react";
import { usePDF } from "react-to-pdf";

// react-to-pdf/jsPDF slice the rendered screenshot into fixed-height pages
// rather than respecting DOM layout, so a section can get cut in half. This
// measures where each `pageBreakRefs` entry currently lands and inserts a
// blank spacer above it so it starts exactly at the top of a page instead.
// Must stay in sync with the page format/orientation/margin passed to toPDF().
const MM_TO_PX = 3.77952755906;
const PDF_PAGE_WIDTH_MM = 210; // A4 portrait
const PDF_PAGE_HEIGHT_MM = 297; // A4 portrait
const PDF_PAGE_MARGIN_MM = 0;

interface UsePdfExportOptions {
  // Sections that should always start on a new PDF page, in the order they
  // appear in the DOM.
  pageBreakRefs?: RefObject<HTMLElement>[];
  filename: string;
  // Runs after isPDFBeingDownloaded is set but before the settle delay, e.g.
  // to prefetch data a PDF-only render path depends on.
  beforeCapture?: () => Promise<unknown> | void;
  // How long to wait for collapsed sections/carousels/dropdowns to expand
  // and render before the screenshot is taken.
  settleDelayMs?: number;
}

// Strips characters that are invalid (or awkward) in a downloaded filename
// on Windows/macOS, e.g. a team name containing a "/" or ":".
export const sanitizeForFilename = (value: string) =>
  value.replace(/[/\\?%*:|"<>]/g, "-").trim();

export default function usePdfExport({
  pageBreakRefs = [],
  filename,
  beforeCapture,
  settleDelayMs = 1500,
}: UsePdfExportOptions) {
  const { toPDF, targetRef } = usePDF();
  const [isPDFBeingDownloaded, setIsPDFBeingDownloaded] = useState(false);
  const [pageBreakSpacers, setPageBreakSpacers] = useState<number[]>(
    pageBreakRefs.map(() => 0)
  );

  const computePageBreakSpacers = () => {
    const wrapper = targetRef.current as HTMLElement | null;
    if (!wrapper) return;

    const wrapperWidthPx = wrapper.getBoundingClientRect().width;
    const pageAvailableWidthPx =
      (PDF_PAGE_WIDTH_MM - 2 * PDF_PAGE_MARGIN_MM) * MM_TO_PX;
    const horizontalFitFactor =
      wrapperWidthPx > pageAvailableWidthPx
        ? wrapperWidthPx / pageAvailableWidthPx
        : 1;
    const pageHeightPx =
      (PDF_PAGE_HEIGHT_MM - 2 * PDF_PAGE_MARGIN_MM) *
      MM_TO_PX *
      horizontalFitFactor;

    const wrapperTop = wrapper.getBoundingClientRect().top;
    let cumulativeSpacer = 0;
    const spacers = pageBreakRefs.map((ref) => {
      const el = ref.current;
      if (!el) return 0;

      const originalTop = el.getBoundingClientRect().top - wrapperTop;
      const effectiveTop = originalTop + cumulativeSpacer;
      const offsetIntoPage = effectiveTop % pageHeightPx;

      // Already sitting at (or very near) the top of a page, no break needed.
      if (offsetIntoPage < 1) return 0;

      const spacer = pageHeightPx - offsetIntoPage;
      cumulativeSpacer += spacer;
      return spacer;
    });

    setPageBreakSpacers(spacers);
  };

  const downloadPDF = async () => {
    setIsPDFBeingDownloaded(true);
    await beforeCapture?.();
    await new Promise((resolve) => setTimeout(resolve, settleDelayMs));
    if (pageBreakRefs.length) {
      computePageBreakSpacers();
      // Let the spacer divs render before the screenshot is taken.
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    await toPDF({ filename });
    setIsPDFBeingDownloaded(false);
    setPageBreakSpacers(pageBreakRefs.map(() => 0));
  };

  return {
    targetRef,
    isPDFBeingDownloaded,
    pageBreakSpacers,
    downloadPDF,
  };
}
